/**
 * Compare two sortKeys using simple byte-order comparison (not localeCompare).
 * The fractional-indexing library generates base-62 keys (0-9, A-Z, a-z) that
 * rely on ASCII ordering where uppercase < lowercase. localeCompare breaks this.
 *
 * Lives apart from `sort-key.ts` so pure consumers (e.g. dashboard sorting)
 * don't pull in the ESM-only `fractional-indexing` generator dependency.
 */
export function compareSortKeys(a: string | undefined, b: string | undefined): number {
  const ak = a ?? '';
  const bk = b ?? '';
  if (ak < bk) return -1;
  if (ak > bk) return 1;
  return 0;
}

/**
 * The canonical visible-card order: `sortKey`, then `id`.
 *
 * The id tiebreak is load-bearing. Two offline devices appending to the same
 * deck mint identical keys (`generateKeyBetween` is deterministic), and until
 * `backfillSortKeys` repairs the duplicate a plain key compare falls through to
 * the caller's array order — the sync store's map order, which is
 * cardId-lexicographic and differs per device. Position math (insert-between,
 * "card N of M", occlusion indices) must not.
 *
 * Every surface that renders or exports a deck's cards sorts with THIS — web
 * through `scoped-deck/cards` `getVisibleSorted`, mobile through `db/hooks`
 * `sortCardsByKey` — so the two platforms cannot disagree on card order.
 */
export function compareCardOrder(
  a: { id: string; sortKey?: string },
  b: { id: string; sortKey?: string },
): number {
  return compareSortKeys(a.sortKey, b.sortKey) || compareSortKeys(a.id, b.id);
}
